import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/store';
import type { StatDef, Statistics } from '../lib/types';
import { ErrorState, LoadingGrid, Panel } from '../components/ui';
import { formatDate, formatNumber, titleCase } from '../lib/format';

/**
 * The statistics page.
 *
 * The client renders whatever list the server hands it and names not one
 * statistic — groups, labels, formats and order all arrive as data. That is
 * what makes adding a statistic a server-side config change rather than a
 * release of this file, and it is the same contract badges and ribbons use for
 * their artwork.
 *
 * Formatting is the one thing that has to live here, because "4,120" and
 * "68%" and "3h 20m" are presentation. Hence `format` on the catalog entry: the
 * server says what kind of number it is, not how to print it.
 */

export function StatisticsPage() {
  const { username } = useParams();
  const state = useAsync<Statistics>(
    () => api.get(username ? `/statistics/trainer/${username}` : '/statistics'),
    [username],
  );

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <LoadingGrid count={6} height={110} />;

  const { catalog, values, joinedAt } = state.data;

  // Groups in first-appearance order, so reordering the page is reordering the
  // catalog rather than editing a list of group names in two places.
  const groups: string[] = [];
  for (const stat of catalog) if (!groups.includes(stat.group)) groups.push(stat.group);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">
            {state.data.trainer ? `${state.data.trainer.username}'s Statistics` : 'Statistics'}
          </h1>
          <div className="page-head__sub">Everything the game remembers. Trainer since {formatDate(joinedAt)}.</div>
        </div>
      </div>

      {groups.map((group) => (
        <Panel key={group} title={group} raised={false}>
          <div className="stats">
            {catalog
              .filter((stat) => stat.group === group)
              .map((stat) => (
                <StatTile key={stat.key} stat={stat} value={values[stat.key] ?? null} />
              ))}
          </div>
        </Panel>
      ))}

      <p className="tiny muted">
        Every figure here is counted from the game's own records when you open this page —
        nothing is stored as a running total, so nothing here can quietly drift out of date.
      </p>
    </div>
  );
}

function StatTile({ stat, value }: { stat: StatDef; value: number | string | null }) {
  return (
    <div className="stat-tile pxwell" title={stat.hint}>
      <div className="stat-tile__value mono">{render(stat, value)}</div>
      <div className="stat-tile__label tiny">{stat.label}</div>
      {stat.hint && <div className="stat-tile__hint tiny muted">{stat.hint}</div>}
    </div>
  );
}

function render(stat: StatDef, value: number | string | null): string {
  if (value === null || value === undefined || value === '') return '—';

  switch (stat.format) {
    case 'percent':
      return `${Number(value)}%`;
    case 'coins':
      return formatNumber(Number(value));
    case 'duration': {
      // Minutes in, something a person would say out loud back out.
      const minutes = Number(value);
      if (minutes < 60) return `${minutes}m`;
      const hours = Math.floor(minutes / 60);
      if (hours < 48) return `${hours}h ${minutes % 60}m`;
      return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    }
    case 'text':
      // Type names and family names arrive as slugs or as "water (14)"; the
      // leading word is title-cased and the parenthetical is left alone.
      return typeof value === 'string' ? titleCase(value) : String(value);
    case 'count':
    default:
      return formatNumber(Number(value));
  }
}
